import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { getAccessToken, websocketUrl } from "./api";
import type { RealtimePayload } from "./types";

type Listener = (payload: RealtimePayload) => void;
type RealtimeContextValue = {
  status: "disconnected" | "connecting" | "connected";
  subscribe: (listener: Listener) => () => void;
  send: (payload: Record<string, unknown>) => void;
  joinChannel: (slug: string) => void;
  presence: Record<string, { online: boolean; lastSeenAt?: string; username?: string }>;
};

function presencePayload() {
  let timeZone = "UTC";
  try {
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    // Keep the server's safe fallback when a browser does not expose its zone.
  }
  let deviceId = "web-session";
  try {
    deviceId = window.localStorage.getItem("versecomms.device_id") || crypto.randomUUID();
    window.localStorage.setItem("versecomms.device_id", deviceId);
  } catch {
    // Session presence still works when storage is unavailable.
  }
  return {
    type: "presence_ping",
    timeZone,
    clientLocalTime: new Date().toISOString(),
    deviceId,
    deviceName: "VerseComms web",
    platform: window.verseDesktop?.isDesktop ? "electron" : "web",
  };
}

const RealtimeContext = createContext<RealtimeContextValue | null>(null);

export function RealtimeProvider({ children }: { children: React.ReactNode }) {
  const socket = useRef<WebSocket | null>(null);
  const listeners = useRef(new Set<Listener>());
  const joined = useRef(new Set<string>());
  const timer = useRef<number | undefined>(undefined);
  const heartbeat = useRef<number | undefined>(undefined);
  const reconnectAttempt = useRef(0);
  const disposed = useRef(false);
  const seenEvents = useRef(new Map<string, number>());
  const pendingMessages = useRef<Record<string, unknown>[]>([]);
  const [presence, setPresence] = useState<RealtimeContextValue["presence"]>({});
  const [status, setStatus] = useState<RealtimeContextValue["status"]>("disconnected");

  const connect = useCallback(() => {
    if (!getAccessToken() || socket.current?.readyState === WebSocket.OPEN || socket.current?.readyState === WebSocket.CONNECTING) return;
    setStatus("connecting");
    const ws = new WebSocket(websocketUrl());
    socket.current = ws;
    ws.onopen = () => {
      reconnectAttempt.current = 0;
      setStatus("connected");
      ws.send(JSON.stringify(presencePayload()));
      joined.current.forEach(slug => ws.send(JSON.stringify({ type: "channel_join", slug })));
      const queued = pendingMessages.current.splice(0);
      queued.forEach(payload => ws.send(JSON.stringify(payload)));
      window.clearInterval(heartbeat.current);
      heartbeat.current = window.setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(presencePayload()));
      }, 20000);
    };
    ws.onmessage = event => {
      try {
        const payload = JSON.parse(event.data) as RealtimePayload;
        if (payload.type === "presence" && typeof payload.userId === "string") {
          setPresence(current => ({
            ...current,
            [payload.userId as string]: {
              online: payload.online === true,
              lastSeenAt: typeof payload.lastSeenAt === "string" ? payload.lastSeenAt : undefined,
              username: typeof payload.username === "string" ? payload.username : undefined,
            },
          }));
        }
        const candidate = payload as unknown as Record<string, unknown>;
        const message = candidate.message as Record<string, unknown> | undefined;
        const eventId = String(candidate.eventId || candidate.id || message?.id || message?._id || "");
        const eventType = String(candidate.type || "");
        if (eventId && eventType) {
          const key = `${eventType}:${eventId}`;
          const now = Date.now();
          for (const [oldKey, timestamp] of seenEvents.current) {
            if (now - timestamp > 120000) seenEvents.current.delete(oldKey);
          }
          if (seenEvents.current.has(key)) return;
          seenEvents.current.set(key, now);
        }
        listeners.current.forEach(listener => listener(payload));
      } catch {
        // Ignore malformed messages from an older server build.
      }
    };
    ws.onclose = () => {
      if (socket.current === ws) socket.current = null;
      window.clearInterval(heartbeat.current);
      setStatus("disconnected");
      window.clearTimeout(timer.current);
      if (!disposed.current && getAccessToken()) {
        const delay = Math.min(30000, 1000 * 2 ** reconnectAttempt.current) + Math.floor(Math.random() * 500);
        reconnectAttempt.current += 1;
        timer.current = window.setTimeout(connect, delay);
      }
    };
  }, []);

  useEffect(() => {
    disposed.current = false;
    connect();
    return () => {
      disposed.current = true;
      window.clearTimeout(timer.current);
      window.clearInterval(heartbeat.current);
      socket.current?.close(1000, "page closed");
    };
  }, [connect]);

  useEffect(() => {
    const onLogout = () => {
      disposed.current = true;
      window.clearTimeout(timer.current);
      window.clearInterval(heartbeat.current);
      socket.current?.close(1000, "signed out");
      socket.current = null;
      pendingMessages.current = [];
      setStatus("disconnected");
    };
    const onLogin = () => {
      disposed.current = false;
      connect();
    };
    window.addEventListener("versecomms:logout", onLogout);
    window.addEventListener("versecomms:login", onLogin);
    return () => {
      window.removeEventListener("versecomms:logout", onLogout);
      window.removeEventListener("versecomms:login", onLogin);
    };
  }, [connect]);

  const send = useCallback((payload: Record<string, unknown>) => {
    if (socket.current?.readyState === WebSocket.OPEN) {
      socket.current.send(JSON.stringify(payload));
    } else {
      pendingMessages.current.push(payload);
      connect();
    }
  }, [connect]);

  const subscribe = useCallback((listener: Listener) => {
    listeners.current.add(listener);
    return () => listeners.current.delete(listener);
  }, []);

  const joinChannel = useCallback((slug: string) => {
    joined.current.add(slug);
    send({ type: "channel_join", slug });
  }, [send]);

  const value = useMemo<RealtimeContextValue>(() => ({
    status,
    subscribe,
    send,
    joinChannel,
    presence,
  }), [joinChannel, presence, send, status, subscribe]);

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

export function usePresence(userId?: string) {
  const { presence } = useRealtime();
  return userId ? presence[userId] : undefined;
}

export function useRealtime() {
  const value = useContext(RealtimeContext);
  if (!value) throw new Error("useRealtime must be used inside RealtimeProvider");
  return value;
}
